import { MoreVertical } from "lucide-react";
import { useState } from "react";

import { toast } from "@/components/toast/toast-queue";

import { useClickOutside } from "../hooks/useClickOutside";
import {
  type CannotDeleteCommentVariant,
  getCannotDeleteCommentVariant,
  useDeleteChatComment,
} from "../hooks/useDeleteChatComment";
import { CannotDeleteCommentModal } from "./CannotDeleteCommentModal";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";

interface MyMessageCardProps {
  children: React.ReactNode;
  commentBatchId: string;
  isEdited?: boolean;
  messageId: number;
  onEditClick?: (messageId: number, commentBatchId: string) => void;
  rawDate: string;
  timestamp: string;
}

export function MyMessageCard({
  children,
  commentBatchId,
  isEdited = false,
  messageId,
  onEditClick,
  rawDate,
  timestamp,
}: MyMessageCardProps) {
  const [_isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cannotDeleteVariant, setCannotDeleteVariant] =
    useState<CannotDeleteCommentVariant | null>(null);

  const deleteComment = useDeleteChatComment();
  const menuRef = useClickOutside(() => setMenuOpen(false));

  const handleDeleteClick = () => {
    setMenuOpen(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteComment.mutate(commentBatchId, {
      onError: (error) => {
        setDeleteModalOpen(false);

        const variant = getCannotDeleteCommentVariant(error);

        if (variant) {
          setCannotDeleteVariant(variant);
        } else {
          toast.add(
            {
              description: error.message || "Please try again later",
              title: "Error deleting message",
              type: "error",
            },
            {
              timeout: 5000,
            },
          );
        }
      },
      onSuccess: () => {
        setDeleteModalOpen(false);
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    deleteComment.reset();
  };

  const handleCannotDeleteClose = () => {
    setCannotDeleteVariant(null);
    deleteComment.reset();
  };

  return (
    <>
      <div
        className="w-full flex justify-end message-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex w-full max-w-[328px] w-[328px] min-w-0">
          <div className="flex flex-col w-full min-w-0">
            <div className="flex items-center justify-between pb-2.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-text-primary">
                  You
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-text-tertiary" title={rawDate}>
                  {timestamp}
                </span>
                {isEdited && (
                  <span className="text-xs text-text-tertiary">(Edited)</span>
                )}
                <div className="relative" ref={menuRef}>
                  <button
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:opacity-80 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setMenuOpen(!menuOpen);
                    }}
                    type="button"
                  >
                    <MoreVertical className="w-4 h-4 text-text-tertiary" />
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-1 w-[164px] bg-white rounded-md shadow-lg border border-button-secondary-border z-10 cursor-pointer">
                      <div className="py-1">
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={(e) => {
                            e.preventDefault();
                            onEditClick?.(messageId, commentBatchId);
                            setMenuOpen(false);
                          }}
                          type="button"
                        >
                          Edit comment
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={handleDeleteClick}
                          type="button"
                        >
                          Delete comment
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-[14px] py-[10px] text-sm my-message-card rounded-tl-sm rounded-br-sm rounded-bl-xl overflow-hidden">
              {children}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isLoading={deleteComment.isPending}
        isOpen={deleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <CannotDeleteCommentModal
        isOpen={cannotDeleteVariant !== null}
        onClose={handleCannotDeleteClose}
        variant={cannotDeleteVariant ?? "feedback-blocked"}
      />
    </>
  );
}
